import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Lock, Loader2, KeyRound, ShieldCheck, Fingerprint } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { setNewPassword, unlockWithPassword, unlockWithRecoveryPhrase, VaultUnlockError } from "@/lib/vault/vaultClient";
import {
  isWebauthnPrfSupported,
  listWebauthnCredentials,
  unlockWithWebauthn,
  WebauthnUnlockError,
} from "@/lib/vault/webauthn";

interface Props {
  open: boolean;
  onUnlocked: () => void;
}

type Phase = "unlock" | "reset-password";
type Mode = "biometric" | "password-or-recovery";

export default function VaultUnlockModal({ open, onUnlocked }: Props) {
  const [tab, setTab] = useState<"password" | "recovery">("password");
  const [phase, setPhase] = useState<Phase>("unlock");
  const [mode, setMode] = useState<Mode>("password-or-recovery");
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwdConfirm, setNewPwdConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [biometricChecked, setBiometricChecked] = useState(false);
  // True while we're still figuring out whether this device has a passkey.
  // During this window we render a minimal "Unlocking your vault…" splash so
  // a password form doesn't flash in front of the user before we auto-fire
  // biometric. Starts true so the very first paint after login is the splash.
  const [probingBiometric, setProbingBiometric] = useState(true);
  // True after auto-fire silently failed (Apple WebKit user-activation rule
  // in regular Safari tabs, or a browser that aborted without user input).
  // We surface ONE big "Tap to unlock" button and never a red error in this
  // case — the user simply hasn't been asked yet.
  const [needsBiometricTap, setNeedsBiometricTap] = useState(false);
  // Guards so a single mount can only auto-fire biometric exactly once. If
  // the user dismisses Face ID and we re-rendered for any reason, we MUST
  // NOT prompt again automatically — that would be an infinite loop.
  const autoFiredRef = useRef(false);

  // On open, see if this device has any enrolled WebAuthn credentials for
  // the current user. If yes and the browser supports PRF, default to the
  // biometric-first path. Otherwise stay on the password/recovery tabs.
  useEffect(() => {
    if (!open || biometricChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const supported = await isWebauthnPrfSupported();
        if (!supported) {
          if (!cancelled) {
            setBiometricChecked(true);
            setProbingBiometric(false);
          }
          return;
        }
        const creds = await listWebauthnCredentials();
        if (cancelled) return;
        if (creds.length > 0) {
          setMode("biometric");
        } else {
          setProbingBiometric(false);
        }
        setBiometricChecked(true);
      } catch {
        if (!cancelled) {
          setBiometricChecked(true);
          setProbingBiometric(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, biometricChecked]);

  // Task #156: auto-fire biometric the moment we land on the biometric mode,
  // so the user doesn't have to tap an extra "Unlock with biometric" button
  // before Face ID / Touch ID / Windows Hello prompts. `auto=true` means
  // "I was called by the effect, not a click": on NotAllowedError/AbortError
  // we silently render a single big tap-to-unlock button instead of a red
  // error toast. iOS Safari in a regular tab requires user activation and
  // will throw NotAllowedError here — that's the path that lands on the
  // big-button fallback, exactly one tap.
  const handleBiometricUnlock = async (auto = false) => {
    setError(null);
    setBusy(true);
    try {
      await unlockWithWebauthn();
      onUnlocked();
    } catch (err: any) {
      const isCancel =
        err instanceof WebauthnUnlockError && err.message === "Biometric cancelled.";
      if (auto && isCancel) {
        // Browser blocked auto-fire (Apple user-activation rule, etc.).
        // Show a single big button; no red error.
        setNeedsBiometricTap(true);
      } else if (isCancel) {
        // User tapped, then dismissed the prompt — let them try again with
        // no error noise. Keep the big tap button visible.
        setNeedsBiometricTap(true);
      } else {
        const msg = err instanceof WebauthnUnlockError ? err.message : "Quick unlock failed.";
        setError(msg);
        setNeedsBiometricTap(true);
      }
    } finally {
      setBusy(false);
      setProbingBiometric(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (mode !== "biometric") return;
    if (autoFiredRef.current) return;
    // Fire on the next tick so the dialog has mounted before the platform
    // biometric sheet appears on top of it. We set the one-shot guard
    // INSIDE the timeout callback (not at scheduling time) so that React 18
    // StrictMode's mount-effect replay — which runs setup, cleanup, setup
    // back-to-back and would otherwise clear the first timeout and then
    // see the ref already set on the second setup — still ends up firing
    // exactly once. The cleanup cancels the pending timeout, and the
    // second setup re-arms it.
    const id = window.setTimeout(() => {
      autoFiredRef.current = true;
      void handleBiometricUnlock(true);
    }, 0);
    return () => window.clearTimeout(id);
    // Intentional: auto-attempt biometric unlock only when the modal opens;
    // handleBiometricUnlock is recreated each render and must not retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  // Reset the one-shot guard when the modal fully closes so a future
  // re-open (e.g. after lock-now → re-unlock) can auto-fire again.
  useEffect(() => {
    if (!open) {
      autoFiredRef.current = false;
      setBiometricChecked(false);
      setProbingBiometric(true);
      setNeedsBiometricTap(false);
      setMode("password-or-recovery");
    }
  }, [open]);

  const handleUnlock = async () => {
    setError(null);
    setBusy(true);
    try {
      if (tab === "password") {
        await unlockWithPassword(password);
        setPassword("");
        setPhrase("");
        onUnlocked();
      } else {
        // Recovery-phrase unlock — typically used because the user forgot
        // their password. Offer to set a new one before continuing.
        await unlockWithRecoveryPhrase(phrase);
        setPassword("");
        setPhrase("");
        setPhase("reset-password");
      }
    } catch (err: any) {
      setError(err instanceof VaultUnlockError ? err.message : "Could not unlock vault.");
    } finally {
      setBusy(false);
    }
  };

  const handleSetNewPassword = async () => {
    setError(null);
    if (newPwd.length < 8) {
      setError("Use at least 8 characters.");
      return;
    }
    if (newPwd !== newPwdConfirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await setNewPassword(newPwd);
      setNewPwd("");
      setNewPwdConfirm("");
      onUnlocked();
    } catch (err: any) {
      setError(err?.message ?? "Could not set new password.");
    } finally {
      setBusy(false);
    }
  };

  const handleSkipReset = () => {
    setNewPwd("");
    setNewPwdConfirm("");
    onUnlocked();
  };

  const switchToPassword = () => {
    setMode("password-or-recovery");
    setProbingBiometric(false);
    setNeedsBiometricTap(false);
    setError(null);
  };

  // Pick a label that matches the platform the user is on, so the big
  // fallback button reads "Tap to unlock with Face ID" on iPhone, "with
  // Windows Hello" on Windows, etc. Pure UA sniff — only affects copy,
  // never gates security behavior.
  const biometricLabel = (() => {
    if (typeof navigator === "undefined") return "biometric";
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua)) return "Face ID";
    if (/Macintosh/.test(ua)) return "Touch ID";
    if (/Windows/.test(ua)) return "Windows Hello";
    if (/Android/.test(ua)) return "biometric";
    return "biometric";
  })();

  // True while we should hide all dialog chrome (header, description) and
  // show a centered spinner instead. Covers the credential-probe window AND
  // the auto-fire window — the user shouldn't see a button they're about to
  // get past automatically.
  const showSplash =
    phase === "unlock" &&
    mode === "biometric" &&
    !needsBiometricTap &&
    (probingBiometric || busy);

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-md" data-testid="dialog-vault-unlock">
        {showSplash ? (
          // Chrome-free splash: visually-hidden title (required by Radix for
          // a11y) + a tiny centered spinner. The platform biometric sheet is
          // about to (or already did) appear on top of us.
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Unlocking your vault</DialogTitle>
              <DialogDescription>
                Waiting for {biometricLabel} to confirm it's you.
              </DialogDescription>
            </DialogHeader>
            <div
              className="flex flex-col items-center justify-center gap-3 py-6"
              data-testid="vault-unlock-splash"
            >
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Unlocking your vault…
              </p>
              {/* Always-available escape hatch — covers the case where the
                  platform biometric sheet never appears or hangs, so the
                  user is never stranded on a spinner. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={switchToPassword}
                data-testid="button-use-password-instead-splash"
              >
                Use password instead
              </Button>
            </div>
          </>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Unlock your vault
          </DialogTitle>
          <DialogDescription>
            ForgeRestore needs your vault key to decrypt your personal recovery
            content on this device. We never see your password or biometric.
          </DialogDescription>
        </DialogHeader>

        {phase === "reset-password" ? (
          <div className="space-y-4">
            <Alert>
              <AlertDescription className="text-sm flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Vault unlocked with your recovery phrase. Set a new password
                  now so you can unlock with a password next time — or skip and
                  keep using your recovery phrase.
                </span>
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label htmlFor="new-vault-pwd">New vault password</Label>
              <Input
                id="new-vault-pwd"
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
                data-testid="input-new-vault-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-vault-pwd-confirm">Confirm new password</Label>
              <Input
                id="new-vault-pwd-confirm"
                type="password"
                value={newPwdConfirm}
                onChange={(e) => setNewPwdConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="input-new-vault-password-confirm"
                onKeyDown={(e) => { if (e.key === "Enter") handleSetNewPassword(); }}
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="text-reset-error">{error}</p>}
            <DialogFooter className="gap-2">
              <Button
                variant="ghost"
                onClick={handleSkipReset}
                disabled={busy}
                data-testid="button-skip-password-reset"
              >
                Skip for now
              </Button>
              <Button
                onClick={handleSetNewPassword}
                disabled={busy}
                data-testid="button-save-new-password"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                {busy ? "Saving…" : "Save new password"}
              </Button>
            </DialogFooter>
          </div>
        ) : mode === "biometric" ? (
          <div className="space-y-4">
            <Button
              onClick={() => handleBiometricUnlock(false)}
              disabled={busy}
              data-testid="button-vault-biometric-unlock"
              className="w-full"
              size="lg"
            >
              {busy ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Fingerprint className="w-5 h-5 mr-2" />
              )}
              {busy ? "Verifying…" : `Tap to unlock with ${biometricLabel}`}
            </Button>
            {error && (
              <p className="text-sm text-destructive" data-testid="text-biometric-error">
                {error}
              </p>
            )}
            <div className="text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={switchToPassword}
                disabled={busy}
                data-testid="button-use-password-instead"
              >
                Use password instead
              </Button>
            </div>
          </div>
        ) : (
        <>
        <Tabs value={tab} onValueChange={(v) => { setTab(v as "password" | "recovery"); setError(null); }}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="password" data-testid="tab-unlock-password">Password</TabsTrigger>
            <TabsTrigger value="recovery" data-testid="tab-unlock-recovery">Recovery phrase</TabsTrigger>
          </TabsList>
          <TabsContent value="password" className="space-y-3 pt-3">
            <Label htmlFor="unlock-pwd">Vault password</Label>
            <Input
              id="unlock-pwd"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="input-unlock-password"
              onKeyDown={(e) => { if (e.key === "Enter") handleUnlock(); }}
            />
          </TabsContent>
          <TabsContent value="recovery" className="space-y-3 pt-3">
            <Label htmlFor="unlock-phrase">Enter your 24-word recovery phrase</Label>
            <Textarea
              id="unlock-phrase"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              rows={3}
              placeholder="word1 word2 word3 …"
              data-testid="input-unlock-phrase"
            />
          </TabsContent>
        </Tabs>

        {error && <p className="text-sm text-destructive" data-testid="text-unlock-error">{error}</p>}

        <DialogFooter>
          <Button onClick={handleUnlock} disabled={busy} data-testid="button-unlock-vault">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            {busy ? "Unlocking…" : "Unlock"}
          </Button>
        </DialogFooter>
        </>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
